import React from 'react';
import ReactDOM from 'react-dom';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import circleToPolygon from 'circle-to-polygon';
import GenericTable from './GenericTable/GenericTable';
import GenericDataFormatter from './GenericTable/GenericDataFormatter';
import config from './config';
import LinkagesDataLayerService from '../../services/LinkagesDataLayer/LinkagesDataLayerService';
import { estados } from '../../util/estados';
import './SocioDemografiaPage.css';
import {Accordion, AccordionTab} from "primereact/accordion";

const TIPOS_INFO_OPTIONS = [
    { label: 'Domicílios por Condição de Ocupação', value: 'domicilios_por_condicao_ocupacao' },
    { label: 'Domicílios por Tipo de Moradia', value: 'domicilios_por_tipo_moradia_simplificado' },
    { label: 'Projeção de Domicílios', value: 'projecao_domicilios' },
    { label: 'Crescimento Anual de Domicílios', value: 'crescimento_domicilios' },
    { label: 'Domicílios por faixa de rendimento familiar', value: 'domicilios_faixa_renda' },
    { label: 'Domicílios por número de moradores', value: 'domicilios_numero_moradores' },
    { label: 'População por Faixa Etária', value: 'populacao_por_faixa_etaria' },
    { label: 'População por Estado Civil', value: 'populacao_por_estado_civil' },
    { label: 'População por Gênero', value: 'populacao_por_genero' },
];

class SocioDemografiaPage extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            cidadesOptions: [],
            projetosOptions: [{ label: 'Sem projeto', value: null }],
            selectedEstados: [],
            selectedCidades: [],
            selectedProjeto: null,
            tipoInfo: 'domicilios_por_condicao_ocupacao',
            overlays: [],
            polygons: [],
            formatter: null,
            loading: false,
        };
    }

    componentDidMount() {
        this.loadProjetos();
    }

    loadProjetos = async () => {
        try {
            const response = await axios.get(route('getMapaProjetos'));
            this.setState({
                projetosOptions: [
                    ...response.data.projects
                ]
            });
        } catch (e) {
            console.error('Erro ao carregar projetos', e);
        }
    }

    loadCidades = async (estadosSiglas) => {
        if (!estadosSiglas.length) return [];
        const response = await axios.post(route('web.municipios_por_estado.index'), { estados: estadosSiglas });
        const options = [];
        response.data.forEach(group => {
            (group.options || []).forEach(city => {
                options.push({ label: city.label, value: `${group.label}-${city.label}` });
            });
        });
        return options;
    }

    onEstadoChange = async (e) => {
        const selectedEstados = e.value;
        this.setState({ selectedEstados, cidadesOptions: [], selectedCidades: [] });
        if (!selectedEstados.length) return;
        try {
            const cidadesOptions = await this.loadCidades(selectedEstados);
            this.setState({ cidadesOptions });
        } catch (err) {
            console.error('Erro ao carregar cidades', err);
        }
    }

    onProjetoChange = async (e) => {
        const projetoId = e.value;
        this.setState({ selectedProjeto: projetoId, overlays: [], polygons: [] });

        if (!projetoId) return;

        try {
            const response = await axios.get(route('mapa.project.show', projetoId));
            const projetoData = response.data.project;

            const { polygons, overlays } = this.buildAreasFromProject(projetoData);

            const cidadeProjeto = JSON.parse(projetoData.cidade) || [];
            const estadoProjeto = JSON.parse(projetoData.estado) || [];

            // Merge estados do projeto com os já selecionados
            const mergedEstados = [...new Set([...this.state.selectedEstados, ...estadoProjeto])];

            // Recarregar cidades se houver novos estados
            let cidadesOptions = this.state.cidadesOptions;
            const newEstados = estadoProjeto.filter(uf => !this.state.selectedEstados.includes(uf));
            if (newEstados.length > 0) {
                try {
                    cidadesOptions = await this.loadCidades(mergedEstados);
                } catch (err) {
                    console.error('Erro ao carregar cidades do projeto', err);
                }
            }

            // Cidades do projeto: value está no formato "UF-CIDADE" (padrão do mapa)
            // Adicionar às options caso não existam e mesclar na seleção
            const existingValues = new Set(cidadesOptions.map(o => o.value));
            const extraOptions = [];
            cidadeProjeto.forEach(c => {
                if (!existingValues.has(c.value)) {
                    extraOptions.push({ label: c.label, value: c.value });
                }
            });

            const mergedCidadesOptions = [...cidadesOptions, ...extraOptions];
            const projectCityValues = cidadeProjeto.map(c => c.value);
            const mergedCidades = [...new Set([...this.state.selectedCidades, ...projectCityValues])];

            this.setState({
                polygons,
                overlays,
                selectedEstados: mergedEstados,
                cidadesOptions: mergedCidadesOptions,
                selectedCidades: mergedCidades,
            });
        } catch (e) {
            console.error('Erro ao carregar projeto', e);
        }
    }

    buildAreasFromProject(projetoData) {
        const polygons = [];
        const overlays = [];
        let areaIndex = 0;

        if (projetoData?.polignos) {
            try {
                JSON.parse(projetoData.polignos).forEach(polygon => {
                    if (!polygon?.polygon) return;
                    const id = polygon.id ?? `area_${areaIndex++}`;
                    const path = polygon.polygon.map(c => [c[1], c[0]]);
                    path.push(path[0]);
                    polygons.push({ id, geojson: { type: 'Polygon', coordinates: [path] } });
                    const polyCustomName = polygon.nome || (polygon.name != null && isNaN(String(polygon.name)) ? String(polygon.name) : null);
                    overlays.push({ id, type: 'poligono', name: polyCustomName || 'Polígono' });
                });
            } catch (e) {
                console.error('Erro ao processar polígonos do projeto', e);
            }
        }

        if (projetoData?.manualCircles) {
            try {
                JSON.parse(projetoData.manualCircles).forEach(circle => {
                    const lng = parseFloat(circle.center?.lng ?? circle.longitude);
                    const lat = parseFloat(circle.center?.lat ?? circle.latitude);
                    const radius = parseFloat(circle.radius);
                    if (isNaN(lng) || isNaN(lat) || isNaN(radius)) return;
                    const id = circle.id ?? `area_${areaIndex++}`;
                    const customName = circle.label || circle.nome || (circle.name != null && isNaN(String(circle.name)) ? String(circle.name) : null);
                    const name = customName || `Raio ${(radius / 1000).toFixed(1)}km`;
                    polygons.push({ id, geojson: circleToPolygon([lng, lat], radius, 32) });
                    overlays.push({ id, type: 'raio', name });
                });
            } catch (e) {
                console.error('Erro ao processar círculos do projeto', e);
            }
        }

        return { polygons, overlays };
    }

    handleLimpar = () => {
        this.setState({
            selectedEstados: [],
            selectedCidades: [],
            selectedProjeto: null,
            cidadesOptions: [],
            overlays: [],
            polygons: [],
            formatter: null,
            tipoInfo: 'domicilios_por_condicao_ocupacao',
        });
    }

    handleCarregar = async () => {
        this.setState({ loading: true, formatter: null });
        try {
            const cidades = this.state.selectedCidades.map(value => {
                const dashIndex = value.indexOf('-');
                return {
                    uf: value.substring(0, dashIndex),
                    cidade: value.substring(dashIndex + 1),
                    id: value
                };
            });

            const basePayload = {
                polygons: this.state.polygons,
                poligonos: this.state.polygons,
                cidades,
                estados: this.state.selectedEstados,
                field: null,
            };

            let dados;

            if (this.state.tipoInfo === 'projecao_domicilios') {
                const response = await LinkagesDataLayerService.getProjecaoDomicilios(basePayload);
                dados = response.data;
            } else if (this.state.tipoInfo === 'crescimento_domicilios') {
                const response = await LinkagesDataLayerService.getCrescimentoDomicilios(basePayload);
                dados = response.data;
            } else if (this.state.tipoInfo === 'domicilios_faixa_renda') {
                const response = await LinkagesDataLayerService.getDomiciliosFaixaRenda(basePayload);
                dados = response.data;
            } else if (this.state.tipoInfo === 'domicilios_numero_moradores') {
                const response = await LinkagesDataLayerService.getNumeroMoradores(basePayload);
                dados = response.data;
            } else if (this.state.tipoInfo === 'domicilios_por_condicao_ocupacao') {
                // Endpoint dedicado lê as colunas consolidadas de condição de ocupação
                // direto da camada populacao_domicilios. Mapeia os campos granulares
                // (próprio quitado/financiado, cedido/emprestado) para as categorias da tabela.
                const response = await LinkagesDataLayerService.getCondicaoOcupacao(basePayload);
                dados = (response.data || []).map(item => ({
                    ...item,
                    proprios: (Number(item.proprio_quitado) || 0) + (Number(item.proprio_financiado) || 0),
                    alugados: Number(item.alugado) || 0,
                    cedidos: Number(item.cedido_ou_emprestado) || 0,
                    outra_condicao: Number(item.outras_condicoes) || 0,
                }));
            } else if (this.state.tipoInfo === 'domicilios_por_tipo_moradia_simplificado') {
                // Endpoint dedicado já retorna casa/apartamento/outros_tipos agregados.
                const response = await LinkagesDataLayerService.getTipoMoradia(basePayload);
                dados = response.data;
            } else if (this.state.tipoInfo === 'populacao_por_faixa_etaria') {
                const response = await LinkagesDataLayerService.getPopulacaoFaixaEtaria(basePayload);
                dados = response.data;
            } else if (this.state.tipoInfo === 'populacao_por_estado_civil') {
                // Endpoint dedicado já retorna casadas/solteiras/separadas_divorciadas_viuvas agregadas.
                const response = await LinkagesDataLayerService.getPopulacaoEstadoCivil(basePayload);
                dados = response.data;
            } else if (this.state.tipoInfo === 'populacao_por_genero') {
                // Endpoint dedicado já retorna masculino/feminino agregados.
                const response = await LinkagesDataLayerService.getPopulacaoGenero(basePayload);
                dados = response.data;
            }

            const configuracao = config[this.state.tipoInfo];
            const formatter = new GenericDataFormatter(dados, this.state.overlays, configuracao);
            this.setState({ formatter });
        } catch (e) {
            console.error('Erro ao carregar dados', e);
        } finally {
            this.setState({ loading: false });
        }
    }

    render() {
        const {
            cidadesOptions, projetosOptions, selectedEstados, selectedCidades,
            selectedProjeto, tipoInfo, formatter, loading, polygons
        } = this.state;

        const canLoad = selectedEstados.length > 0 || selectedCidades.length > 0 || polygons.length > 0;

        return (
            <div className="content">
                <div className="container-fluid">
                    <div className="card">
                        <div className="card-header">

                        </div>
                        <div className="card-body">
                            <Accordion activeIndex={0}>
                                <AccordionTab header="Filtros">
                                    <form>
                                        <fieldset>
                                            <div className="form-group row">
                                                <div className="col-12 col-sm-6 col-md-6 col-lg-6 col-xl-6">
                                                    <label className="ef-label">Carregar Projeto</label>
                                                    <Dropdown
                                                        value={selectedProjeto}
                                                        options={projetosOptions}
                                                        filter
                                                        filterPlaceholder="Buscar projeto..."
                                                        optionLabel="label"
                                                        optionValue="value"
                                                        onChange={this.onProjetoChange}
                                                        placeholder="Selecione um projeto"
                                                        className="w-100"
                                                    />
                                                </div>
                                            </div>
                                        </fieldset>

                                        <hr />

                                        <fieldset>
                                            <div className="form-group row">
                                                <div className="col-12 col-sm-6 col-md-6 col-lg-6 col-xl-6">
                                                    <label className="ef-label">Estado</label>
                                                    <MultiSelect
                                                        value={selectedEstados}
                                                        options={estados}
                                                        optionLabel="label"
                                                        optionValue="value"
                                                        onChange={this.onEstadoChange}
                                                        placeholder="Selecione o estado"
                                                        filter
                                                        filterPlaceholder="Buscar estado..."
                                                        display="chip"
                                                        className="w-100"
                                                    />
                                                </div>
                                                <div className="col-12 col-sm-6 col-md-6 col-lg-6 col-xl-6">
                                                    <label className="ef-label">Cidade</label>
                                                    <MultiSelect
                                                        value={selectedCidades}
                                                        options={cidadesOptions}
                                                        optionLabel="label"
                                                        optionValue="value"
                                                        onChange={(e) => this.setState({ selectedCidades: e.value })}
                                                        placeholder={selectedEstados.length ? 'Selecione a cidade' : 'Selecione um estado primeiro'}
                                                        disabled={!selectedEstados.length}
                                                        filter
                                                        filterPlaceholder="Buscar cidade..."
                                                        display="chip"
                                                        className="w-100"
                                                    />
                                                </div>
                                            </div>
                                        </fieldset>

                                        <fieldset>
                                            <div className="form-group row">
                                                <div className="col-12 col-sm-6 col-md-6 col-lg-6 col-xl-6">
                                                    <label className="ef-label">Tipo de Informação</label>
                                                    <Dropdown
                                                        value={tipoInfo}
                                                        options={TIPOS_INFO_OPTIONS}
                                                        optionLabel="label"
                                                        optionValue="value"
                                                        onChange={(e) => this.setState({ tipoInfo: e.value })}
                                                        placeholder="Selecione o tipo"
                                                        className="w-100"
                                                    />
                                                </div>
                                            </div>
                                        </fieldset>

                                        <fieldset>
                                            <div className="form-group d-flex" style={{ gap: '8px' }}>
                                                <button
                                                    type="button"
                                                    className="btn btn-success ef-submit-btn"
                                                    onClick={this.handleCarregar}
                                                    disabled={loading || !canLoad}
                                                >
                                                    {loading
                                                        ? <><i className="fa fa-spinner fa-spin" /> Carregando...</>
                                                        : <><i className="fa fa-search" /> Carregar Dados</>
                                                    }
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={this.handleLimpar}
                                                    disabled={loading}
                                                >
                                                    <i className="fa fa-times" /> Limpar
                                                </button>
                                            </div>
                                        </fieldset>
                                    </form>
                                </AccordionTab>

                            </Accordion>

                            <hr />

                            {formatter && !loading && (
                                <GenericTable formatter={formatter} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

if (document.getElementById('sociodemografia-page')) {
    ReactDOM.render(<SocioDemografiaPage />, document.getElementById('sociodemografia-page'));
}
